/**
 * Rettet Formulareingaben über einen erzwungenen Neustart hinweg.
 *
 * WARUM (Noah, 18.09.2026): Caroline Reuter meldete „die Website stürzt hier und da ab".
 * Technisch stürzt sie nicht ab: Nach einem Publish fehlt eine Datei, der Handler in
 * main.tsx lädt die Seite neu, und die Seite steht danach wieder. Was dabei verloren geht,
 * sind die Eingaben — und genau das erlebt eine Kundin als Absturz. Bei 15 Publishes an
 * einem Vormittag trifft das ausgerechnet die, die gerade bucht.
 *
 * Gerettet wird nur, was ein Mensch getippt hat, und nur für kurze Zeit.
 * NIE gesichert: Passwortfelder, Felder mit autocomplete="off"/"new-password" und alles,
 * was ausdrücklich mit data-keine-rettung markiert ist. Kartendaten liegen ohnehin in
 * Stripe-iframes, an die kein Seitencode herankommt.
 */

const KEY = "cc.formularRettung.v1";
/** Ältere Stände werden verworfen: ein Entwurf von gestern gehört nicht ins Formular. */
const MAX_ALTER_MS = 10 * 60 * 1000;

interface Gerettet {
  pfad: string;
  zeit: number;
  felder: Record<string, string>;
}

function feldSchluessel(el: HTMLElement): string | null {
  const name = el.getAttribute("name") || el.getAttribute("id");
  return name ? name : null;
}

function rettbar(el: Element): el is HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement {
  if (!(el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement || el instanceof HTMLSelectElement)) {
    return false;
  }
  if (el.hasAttribute("data-keine-rettung")) return false;
  if (el instanceof HTMLInputElement) {
    if (el.type === "password" || el.type === "hidden" || el.type === "file") return false;
    const ac = (el.getAttribute("autocomplete") || "").toLowerCase();
    if (ac === "off" || ac.includes("password") || ac.includes("cc-")) return false;
  }
  return true;
}

/** Vor einem erzwungenen Neustart aufrufen. Speichert nur, wenn wirklich etwas getippt wurde. */
export function formularZustandSichern(): void {
  try {
    const felder: Record<string, string> = {};
    document.querySelectorAll("input, textarea, select").forEach((el) => {
      if (!rettbar(el)) return;
      const key = feldSchluessel(el);
      if (!key) return;
      if (el instanceof HTMLInputElement && (el.type === "checkbox" || el.type === "radio")) {
        if (el.checked) felder[`${key}:${el.value}`] = "1";
        return;
      }
      if (el.value) felder[key] = el.value;
    });

    if (Object.keys(felder).length === 0) return;
    const daten: Gerettet = { pfad: window.location.pathname, zeit: Date.now(), felder };
    sessionStorage.setItem(KEY, JSON.stringify(daten));
  } catch {
    // sessionStorage kann blockiert sein — dann eben ohne Rettung neu laden.
  }
}

/**
 * Nach dem Neustart aufrufen. Setzt die Werte zurück in die Felder und feuert input/change,
 * damit React (und damit react-hook-form) den Stand übernimmt.
 */
export function formularZustandWiederherstellen(): void {
  try {
    const roh = sessionStorage.getItem(KEY);
    if (!roh) return;
    sessionStorage.removeItem(KEY);

    const daten = JSON.parse(roh) as Gerettet;
    if (!daten?.felder || daten.pfad !== window.location.pathname) return;
    if (Date.now() - daten.zeit > MAX_ALTER_MS) return;

    // Nach dem Reload rendert React erst; ein Tick später stehen die Felder.
    window.setTimeout(() => {
      for (const [key, wert] of Object.entries(daten.felder)) {
        const [name, optionsWert] = key.split(":");
        const el = document.querySelector<HTMLElement>(`[name="${CSS.escape(name)}"], #${CSS.escape(name)}`);
        if (!el || !rettbar(el)) continue;

        if (el instanceof HTMLInputElement && (el.type === "checkbox" || el.type === "radio")) {
          if (optionsWert !== undefined && el.value !== optionsWert) continue;
          el.checked = true;
        } else if (
          el instanceof HTMLInputElement ||
          el instanceof HTMLTextAreaElement ||
          el instanceof HTMLSelectElement
        ) {
          el.value = wert;
        }
        el.dispatchEvent(new Event("input", { bubbles: true }));
        el.dispatchEvent(new Event("change", { bubbles: true }));
      }
    }, 600);
  } catch {
    // Kaputter Eintrag: lieber ohne Wiederherstellung weiter als mit Fehler.
  }
}
